/**
 * Rejected Plans report (Reporting page, "Rejected Plans" tab).
 *
 * One row per rejection — a plan rejected twice appears twice. `GET
 * /api/reports/plan-rejections` returns the whole scoped log in one call, so sorting by
 * date and searching are client-side, matching PipelineReportTable / DecisionLogTable.
 *
 * Creator is the plan's author when it was rejected; Submitter is whoever sent it out
 * (blank when the audit trail can't say). Both are snapshots, so they don't change if the
 * plan is reassigned later.
 */

import { Link } from "@tanstack/react-router";
import { ArrowDown, ArrowUp, Search, Undo2 } from "lucide-react";
import { useMemo, useState } from "react";
import { EmptyState } from "@/components/shared/empty-state";
import { ErrorState } from "@/components/shared/error-state";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { usePlanRejections } from "@/hooks/use-plan-rejections";
import type { PlanRejectionReportRow } from "@/schemas/resource-plans";

const TH_CLASS = "px-3 py-2 font-semibold";
const TD_CLASS = "px-3 py-2 align-top";
const DASH = <span className="text-muted-foreground">—</span>;

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

function matches(row: PlanRejectionReportRow, needle: string): boolean {
  return [
    row.planName,
    row.clientAccountName,
    row.solutionName,
    row.reason,
    row.creatorName,
    row.submitterName,
    row.rejectedByName,
  ].some((v) => v?.toLowerCase().includes(needle));
}

export function PlanRejectionsTable() {
  const { data, isLoading, isError, refetch } = usePlanRejections();
  const [search, setSearch] = useState("");
  const [newestFirst, setNewestFirst] = useState(true);

  const rows = useMemo(() => {
    const needle = search.trim().toLowerCase();
    const filtered = needle === "" ? (data ?? []) : (data ?? []).filter((r) => matches(r, needle));
    return [...filtered].sort((a, b) =>
      newestFirst
        ? b.rejectedAt.localeCompare(a.rejectedAt)
        : a.rejectedAt.localeCompare(b.rejectedAt),
    );
  }, [data, search, newestFirst]);

  if (isLoading) {
    return (
      <div className="space-y-2">
        <Skeleton className="h-9 w-64" />
        <Skeleton className="h-40 w-full" />
      </div>
    );
  }
  if (isError) {
    return <ErrorState message="Couldn't load the Rejected Plans report" onRetry={refetch} />;
  }
  if ((data ?? []).length === 0) {
    return (
      <EmptyState
        icon={Undo2}
        title="No rejected plans"
        description="When a Resource Manager rejects a plan, it is logged here with the reason."
      />
    );
  }

  return (
    <div className="space-y-3">
      <div className="relative max-w-xs">
        <Search className="pointer-events-none absolute top-2.5 left-2.5 h-4 w-4 text-muted-foreground" />
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search plan, account, reason…"
          aria-label="Search rejected plans"
          className="pl-8"
        />
      </div>

      <div className="overflow-x-auto rounded-md border border-border bg-card">
        <table className="w-full text-sm">
          <thead className="bg-muted/40 text-left text-xs uppercase tracking-wider text-muted-foreground">
            <tr>
              <th className={TH_CLASS}>Plan</th>
              <th className={TH_CLASS} aria-sort={newestFirst ? "descending" : "ascending"}>
                <button
                  type="button"
                  className="inline-flex items-center gap-1 uppercase tracking-wider"
                  onClick={() => setNewestFirst((v) => !v)}
                >
                  Rejected on
                  {newestFirst ? (
                    <ArrowDown className="h-3 w-3" />
                  ) : (
                    <ArrowUp className="h-3 w-3" />
                  )}
                </button>
              </th>
              <th className={TH_CLASS} title="The plan's author when it was rejected">
                Creator
              </th>
              <th className={TH_CLASS} title="Who sent the plan out">
                Submitter
              </th>
              <th className={TH_CLASS}>Rejection reason</th>
              <th className={TH_CLASS}>Rejected by</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.id} className="border-t border-border hover:bg-muted/40">
                <td className={TD_CLASS}>
                  <div className="font-medium text-foreground">
                    {row.resourcePlanId === null ? (
                      <span title="This plan has since been deleted">{row.planName}</span>
                    ) : (
                      <Link
                        to="/dashboard/plans/$planId"
                        params={{ planId: row.resourcePlanId }}
                        className="hover:underline"
                      >
                        {row.planName}
                      </Link>
                    )}
                  </div>
                  <div className="text-xs text-muted-foreground">
                    {[row.clientAccountName, row.solutionName].filter(Boolean).join(" · ")}
                  </div>
                </td>
                <td className={`${TD_CLASS} whitespace-nowrap`}>{formatDate(row.rejectedAt)}</td>
                <td className={TD_CLASS}>{row.creatorName ?? DASH}</td>
                <td className={TD_CLASS}>{row.submitterName ?? DASH}</td>
                <td className={`${TD_CLASS} max-w-md whitespace-pre-wrap`}>{row.reason}</td>
                <td className={TD_CLASS}>{row.rejectedByName ?? DASH}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {rows.length === 0 && (
          <p className="px-3 py-6 text-center text-sm text-muted-foreground">
            No rejections match that search.
          </p>
        )}
      </div>
    </div>
  );
}
